import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useTranslation } from 'react-i18next';
import type { DiscountSettings } from '../../models/Organization';
import {
  getSingleBookingDiscountAmount,
  getSingleBookingDiscountedTotal,
} from '../../utils/discounts';

export interface ManualBookingPriceSummaryProps {
  lineLabel: string;
  /** Price of one session before the organization discount. */
  sessionAmount: number;
  durationMinutes: number;
  occurrenceCount?: number;
  currencyCode: string;
  discountSettings?: DiscountSettings | null;
}

const formatAmount = (amount: number, currencyCode: string) =>
  `${amount.toFixed(2)} ${currencyCode}`;

export default function ManualBookingPriceSummary({
  lineLabel,
  sessionAmount,
  durationMinutes,
  occurrenceCount = 1,
  currencyCode,
  discountSettings,
}: ManualBookingPriceSummaryProps) {
  const { t } = useTranslation();
  const sessions = Math.max(1, occurrenceCount);
  if (!lineLabel.trim() || !Number.isFinite(sessionAmount) || durationMinutes <= 0) {
    return null;
  }

  const baseTotal = sessionAmount * sessions;
  const discountAmount = getSingleBookingDiscountAmount(
    baseTotal,
    durationMinutes,
    discountSettings,
    sessions,
  );
  const total = getSingleBookingDiscountedTotal(
    baseTotal,
    durationMinutes,
    discountSettings,
    sessions,
  );
  const singleBookingDiscount = discountSettings?.singleBookingDiscount;
  const billCurrency = currencyCode.trim() || 'PLN';

  return (
    <Box
      data-testid="manual-booking-price-summary"
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        px: 1.5,
        py: 1.25,
      }}
    >
      <Stack spacing={0.75}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {t('payment.billBreakdown', 'Bill Breakdown')}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
          <Typography variant="body2">{lineLabel}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {sessions > 1
              ? `${sessionAmount.toFixed(2)} × ${sessions} = ${formatAmount(baseTotal, billCurrency)}`
              : formatAmount(sessionAmount, billCurrency)}
          </Typography>
        </Stack>
        {discountAmount > 0 ? (
          <Stack
            data-testid="manual-booking-discount"
            direction="row"
            spacing={1}
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <LocalOfferIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="body2" color="success.main">
                {t('payment.singleBookingDiscountAppliedWithThreshold', {
                  defaultValue: '{{percent}}% discount for {{hours}}h+ bookings',
                  percent: singleBookingDiscount?.discountPercent ?? 0,
                  hours: singleBookingDiscount?.minimumBookingHours ?? 0,
                })}
              </Typography>
            </Stack>
            <Typography variant="body2" color="success.main" sx={{ whiteSpace: 'nowrap' }}>
              -{formatAmount(discountAmount, billCurrency)}
            </Typography>
          </Stack>
        ) : null}
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {t('payment.totalAmount', 'Total Amount')}
          </Typography>
          <Typography
            variant="body2"
            color="primary"
            data-testid="manual-booking-total"
            sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            {formatAmount(total, billCurrency)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
